import { useCallback, useMemo } from "react";
import { addDays, differenceInDays, format, fromUnixTime, isSameDay } from "date-fns/esm";
import { StringLike, NumberLike } from "@visx/scale";

import LineChart from "ds/components/Charts/LineChart";
import BarChart from "ds/components/Charts/BarChart";
import { Datum } from "ds/components/Charts/LineChart/types";
import { unixTimestampToMs } from "utils/time";
import { DataPoint } from "types/generated";

import { DoraChartType } from "../useChartType";
import useDoraDateRange from "../useDateRange";
import RangeWrapper from "./RangeWrapper";

const CHART_COLOR = "var(--semantic-color-foreground-info)";

type DoraRangeProps = {
  range: DataPoint[];
  chartType: DoraChartType;
  leftAxisLabel: string;
  formatYAxisLabel: (value: NumberLike) => string;
  formatTooltipValue: (value: number) => string;
  // Optional: converts raw DataPoint value to display value (default: identity)
  convertValue?: (value: number) => number;
  // Grade threshold in display units — bars above this value are highlighted red
  gradeFairMax?: number;
};

const DoraRange = ({
  range,
  chartType,
  leftAxisLabel,
  formatYAxisLabel,
  formatTooltipValue,
  convertValue,
  gradeFairMax,
}: DoraRangeProps) => {
  const { startTime, endTime } = useDoraDateRange();

  const chartData = useMemo(() => {
    const toDisplayValue = convertValue || ((v: number) => v);

    if (chartType === "bar") {
      // Fill all days in the range, zero for missing days
      const days = Array.from({ length: differenceInDays(endTime, startTime) }, (_, i) =>
        addDays(startTime, i + 1)
      );

      return days.map((date): Datum => {
        const point = range.find((p) => isSameDay(fromUnixTime(p.timestamp), date));

        return {
          value: point ? toDisplayValue(point.value) : 0,
          timestamp: date.getTime(),
        };
      });
    }

    // Line chart: only real data points, sorted by time
    return range
      .map(
        (point): Datum => ({
          value: toDisplayValue(point.value),
          timestamp: unixTimestampToMs(point.timestamp),
        })
      )
      .sort((a, b) => a.timestamp - b.timestamp);
  }, [range, chartType, startTime, endTime, convertValue]);

  const formatXAxisLabel = useCallback(
    (value: StringLike) => format(value as number, "E dd MMM"),
    []
  );

  const getBarColor = useMemo(() => {
    if (gradeFairMax === undefined) return undefined;

    return (datum: Datum) => {
      if (datum.value <= gradeFairMax) return CHART_COLOR;

      return "var(--semantic-color-foreground-danger)";
    };
  }, [gradeFairMax]);

  return (
    <RangeWrapper>
      {chartType === "bar" ? (
        <BarChart
          data={chartData}
          items={["value"]}
          colors={[CHART_COLOR]}
          xKey="timestamp"
          formatXAxisLabel={formatXAxisLabel}
          formatYAxisLabel={formatYAxisLabel}
          leftAxisLabel={leftAxisLabel}
          tooltipHeader=""
          formatTooltipValue={(data) => formatTooltipValue(data.value)}
          showItemsInTooltip={false}
          showGridRows={false}
          tooltipReactToScroll
          fixedHeight={360}
          margin={{ left: 50 }}
          getBarColor={getBarColor}
        />
      ) : (
        <LineChart
          data={chartData}
          items={["value"]}
          colors={[CHART_COLOR]}
          xKey="timestamp"
          formatXAxisLabel={formatXAxisLabel}
          formatYAxisLabel={formatYAxisLabel}
          leftAxisLabel={leftAxisLabel}
          renderTooltip={(data) => <>{formatTooltipValue(data.value)}</>}
          tooltipReactToScroll
        />
      )}
    </RangeWrapper>
  );
};

export default DoraRange;
